import { ApiError, obterAccessToken, renovarSessao } from './api.js';
import { slugAtual } from './tenant.js';

/**
 * Download do PDF do orçamento — Story 8.3 (RF-26).
 *
 * Não passa pelo cliente de API porque a resposta é **binária**, não envelope.
 * O caminho é o real de produção: a API enfileira no BullMQ, o worker renderiza
 * no Chromium e a requisição só volta quando o arquivo está pronto — por isso a
 * tela avisa que está gerando em vez de fingir que é instantâneo.
 *
 * O token vai por header, como em toda requisição autenticada: uma URL com
 * token dentro vazaria no histórico do navegador e nos logs do proxy.
 */
export async function baixarPdf(quoteId: string, nomeArquivo: string): Promise<void> {
  function cabecalhos(): Headers {
    const h = new Headers();
    const token = obterAccessToken();
    if (token !== undefined) h.set('authorization', `Bearer ${token}`);
    const slug = slugAtual();
    if (slug !== undefined) h.set('x-tenant-slug', slug);
    return h;
  }

  const pedir = (): Promise<Response> =>
    fetch(`/api/orcamentos/${quoteId}/pdf`, {
      headers: cabecalhos(),
      credentials: 'same-origin',
    });

  let resposta = await pedir();

  /*
   * Renovação de sessão também aqui.
   *
   * Este download não passa pelo `requisitar()` (a resposta é binária), então
   * ficava de fora do refresh automático: o access token dura 15 min e o
   * usuário que voltasse a um orçamento antigo para imprimir recebia um erro
   * de sessão em vez do arquivo — sem nada na tela explicando.
   */
  if (resposta.status === 401) {
    await renovarSessao().catch(() => undefined);
    resposta = await pedir();
  }

  if (!resposta.ok) {
    // Erro aqui vem no envelope (a rota só devolve binário no sucesso).
    let codigo = 'INTERNAL_ERROR';
    let mensagem = 'Não foi possível gerar o PDF.';
    try {
      const corpo = (await resposta.json()) as { error?: { code?: string; message?: string } };
      codigo = corpo.error?.code ?? codigo;
      mensagem = corpo.error?.message ?? mensagem;
    } catch {
      // Resposta sem JSON — mantém a mensagem padrão.
    }
    throw new ApiError(codigo, mensagem, resposta.status);
  }

  const blob = await resposta.blob();
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = `${nomeArquivo}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    // Sem isto o blob fica na memória da aba até o reload.
    URL.revokeObjectURL(url);
  }
}
